import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Card, Tabs, Table, Typography, Select, Button, Space, Modal, Form, Input, InputNumber, Popconfirm, message, Divider, Row, Col, AutoComplete, Tag } from 'antd';
import { AppstoreAddOutlined, WarningOutlined, SyncOutlined, SwapOutlined, PlusOutlined, DeleteOutlined, FilterOutlined, EditOutlined, MinusCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { fetchClusters, fetchClusterInstitutions } from '../../../Service/Clusters_API/clustersApi';
import { fetchAcademicYears } from '../../../Service/Syllabus_API/syllabusApi';
import { 
  fetchNewEquipment, createNewEquipmentInvoice, updateNewEquipmentInvoice, deleteNewEquipmentInvoice,
  fetchMissingItems, createMissingItem, updateMissingItem, deleteMissingItem,
  fetchRemovedItems, createRemovedItem, updateRemovedItem, deleteRemovedItem,
  fetchReplacementOrders, createReplacementOrder, updateReplacementOrder, deleteReplacementOrder, bulkUpdateReplacementStatus,
  renameReplacementType, deleteReplacementType
} from '../../../Service/Equipment_API/equipmentApi';
import { useRealTimeUpdates } from '../../../Hooks/useRealTime';

const { Title } = Typography;
const { Option } = Select;

const EquipmentManagementPage = () => {
  const [activeTab, setActiveTab] = useState('new-equipment');
  const [form] = Form.useForm();
  
  // Global Filters
  const [clusters, setClusters] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [filterCluster, setFilterCluster] = useState(null);
  const [filterAcademicYear, setFilterAcademicYear] = useState(null);

  // Modals & Forms State
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formInstitutions, setFormInstitutions] = useState([]);
  const [customTypes, setCustomTypes] = useState(() => {
    try {
      const saved = localStorage.getItem('equipment_replacement_types');
      return saved ? JSON.parse(saved) : ['PROJECTOR', 'PROJECTOR SCREEN'];
    } catch {
      return ['PROJECTOR', 'PROJECTOR SCREEN'];
    }
  });
  const [deletedTypes, setDeletedTypes] = useState(() => {
    try {
      const saved = localStorage.getItem('equipment_deleted_replacement_types');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [newTypeName, setNewTypeName] = useState('');
  const [editingTypeTarget, setEditingTypeTarget] = useState(null);
  const [isEditTypeModalVisible, setIsEditTypeModalVisible] = useState(false);
  const [filterReplacementStatus, setFilterReplacementStatus] = useState('ALL');
  const [selectedReplacementRowKeys, setSelectedReplacementRowKeys] = useState([]);
  
  const role = localStorage.getItem('role');

  // Form Watch
  const formAcademicYearId = Form.useWatch('academic_year_id', form);
  
  // Data States
  const [newEquipmentData, setNewEquipmentData] = useState([]);
  const [missingItemsData, setMissingItemsData] = useState([]);
  const [removedInstalledData, setRemovedInstalledData] = useState([]);
  const [replacementOrdersData, setReplacementOrdersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setUpdateState] = useState(0);
  const [searchTexts, setSearchTexts] = useState({
    'new-equipment': '',
    'missing-items': '',
    'removed-installed': '',
    'replacement-orders': ''
  });

  useEffect(() => {
    loadInitialFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only load data if academic year is set (or if you want to load regardless, keep it)
    // Actually, to avoid fetching data without year, we could check, but let's keep it as is.
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCluster, filterAcademicYear]);

  // Auto-default cluster for STUDIO role
  useEffect(() => {
    if (role === 'STUDIO') {
      const available = getAvailableClusters(filterAcademicYear);
      if (available.length > 0) {
        if (!filterCluster || !available.some(c => c.cluster_id === filterCluster)) {
          setFilterCluster(available[0].cluster_id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, clusters, filterAcademicYear, filterCluster]);

  const loadInitialFilters = async () => {
    try {
      const [clusData, ayData] = await Promise.all([
        fetchClusters().catch(() => []),
        fetchAcademicYears(true).catch(() => [])
      ]);
      setClusters(clusData || []);
      setAcademicYears(ayData || []);
      
      if (ayData && ayData.length > 0) {
        const activeAy = ayData.find(y => y.status === 'Active' || y.status === 'ACTIVE' || !y.status);
        if (activeAy) {
          setFilterAcademicYear(activeAy.academic_year_id);
        } else {
          setFilterAcademicYear(ayData[ayData.length - 1].academic_year_id);
        }
      }
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [newEq, missing, removed, replace] = await Promise.all([
        fetchNewEquipment(undefined, filterAcademicYear).catch(() => []),
        fetchMissingItems(filterCluster, filterAcademicYear).catch(() => []),
        fetchRemovedItems(filterCluster, filterAcademicYear).catch(() => []),
        fetchReplacementOrders(filterCluster, filterAcademicYear).catch(() => [])
      ]);
      setNewEquipmentData(newEq || []);
      setMissingItemsData(missing || []);
      setRemovedInstalledData(removed || []);
      setReplacementOrdersData(replace || []);
    } catch (error) {
      message.error('Failed to fetch data');
    }
    setLoading(false);
  };

  // Hook into real-time updates for the equipment entity
  useRealTimeUpdates('equipment', loadAllData);

  const getAvailableClusters = (ayId) => {
    if (!ayId) return clusters;
    return clusters.filter(c => !c.academic_year_id || String(c.academic_year_id) === String(ayId));
  };

  const loadInstitutionsForForm = async (clusterIdInput, academicYearId) => {
    try {
      if (!clusterIdInput || clusterIdInput.length === 0) { setFormInstitutions([]); return; }
      
      const clusterIds = Array.isArray(clusterIdInput) ? clusterIdInput : [clusterIdInput];
      const allInstData = await Promise.all(
        clusterIds.map(id => fetchClusterInstitutions(id, academicYearId))
      );
      
      const combined = allInstData.flat().filter(Boolean);
      setFormInstitutions(combined);
    } catch (err) {
      setFormInstitutions([]);
    }
  };

  const handleClusterSelect = (val) => {
    const currentInstalled = form.getFieldValue('installed_cluster_id');
    const idsToLoad = [];
    if (val) idsToLoad.push(val);
    if (currentInstalled) idsToLoad.push(currentInstalled);
    loadInstitutionsForForm(idsToLoad, formAcademicYearId);
  };

  const handleInstalledClusterSelect = (val) => {
    const currentCluster = form.getFieldValue('cluster_id');
    const idsToLoad = [];
    if (currentCluster) idsToLoad.push(currentCluster);
    if (val) idsToLoad.push(val);
    loadInstitutionsForForm(idsToLoad, formAcademicYearId);
  };

  const handleRowClusterChange = async (val, fieldName) => {
    const items = form.getFieldValue('items') || [];
    if (!items[fieldName]) items[fieldName] = {};
    const cname = clusters.find(c => String(c.cluster_id) === String(val))?.cluster_name || '';
    items[fieldName] = { ...items[fieldName], cluster_name: cname, cluster_id: val };
    form.setFieldsValue({ items });

    if (val) {
      try {
        const instData = await fetchClusterInstitutions(val, formAcademicYearId);
        if (instData) {
          setFormInstitutions(prev => {
            const newInst = [...prev];
            instData.forEach(inst => {
              if (!newInst.find(i => String(i.cluster_institution_id) === String(inst.cluster_institution_id))) {
                newInst.push(inst);
              }
            });
            return newInst;
          });
        }
      } catch (e) {}
    }
    setUpdateState(prev => prev + 1);
  };

  const handleValuesChange = (changedValues, allValues) => {
    if (modalType !== 'new-equipment') return;

    let recalculateTotals = false;
    let items = [...(allValues.items || [])];

    if (changedValues.items) {
      changedValues.items.forEach((item, index) => {
        if (item && (item.rate !== undefined || item.nos !== undefined || item.gst_percent !== undefined)) {
          const row = items[index] || {};
          const rate = parseFloat(row.rate) || 0;
          const nos = parseFloat(row.nos) || 0;
          const price = rate * nos;
          items[index] = { ...row, price: price > 0 ? price.toFixed(2) : '' };
          recalculateTotals = true;
        }
      });
      if (recalculateTotals) {
        form.setFieldsValue({ items });
      }
    }

    if (changedValues.items || changedValues.discount !== undefined) {
      const currentItems = form.getFieldValue('items') || [];
      let subTotal = 0;
      let totalGst = 0;

      currentItems.forEach(item => {
        const price = parseFloat(item?.price) || 0;
        const gstPercent = parseFloat(item?.gst_percent) || 0;
        subTotal += price;
        totalGst += price * (gstPercent / 100);
      });

      const discount = parseFloat(form.getFieldValue('discount')) || 0;
      const amountAfterDiscount = subTotal - discount;
      const totalBeforeRounding = amountAfterDiscount + totalGst;
      
      const grandTotal = Math.round(totalBeforeRounding);
      const roundOff = grandTotal - totalBeforeRounding;

      form.setFieldsValue({
        sub_total: subTotal > 0 ? subTotal.toFixed(2) : '',
        total_gst: totalGst > 0 ? totalGst.toFixed(2) : '',
        round_off: (subTotal > 0 || totalGst > 0) ? roundOff.toFixed(2) : '',
        grand_total: grandTotal > 0 ? grandTotal.toFixed(2) : ''
      });
    }
  };

  const handleDelete = async (key) => {
    try {
      if (activeTab === 'missing-items') await deleteMissingItem(key);
      else if (activeTab === 'removed-installed') await deleteRemovedItem(key);
      else if (activeTab === 'replacement-orders') await deleteReplacementOrder(key);
      
      message.success('Item deleted successfully');
      loadAllData();
    } catch (error) {
      message.error('Failed to delete item');
    }
  };

  const openAddModal = (type) => {
    setModalType(type);
    setIsEditing(false);
    setEditingId(null);
    form.resetFields();
    
    // Set default filter context if available
    let initialValues = {};
    if (filterCluster) initialValues.cluster_id = filterCluster;
    if (filterAcademicYear) initialValues.academic_year_id = filterAcademicYear;
    
    if (type === 'new-equipment') {
      initialValues.items = [{ cluster_id: filterCluster || undefined, nos: 1 }];
    }
    if (type === 'replacement-orders') {
      initialValues.status = 'Pending';
    }
    form.setFieldsValue(initialValues);
    setIsModalVisible(true);
    if (filterCluster) {
      loadInstitutionsForForm(filterCluster, filterAcademicYear);
    }
  };

  const handleBulkStatusUpdate = async (statusToSet = 'Ordered') => {
    if (selectedReplacementRowKeys.length === 0) return;
    try {
      setLoading(true);
      await bulkUpdateReplacementStatus({ ids: selectedReplacementRowKeys, status: statusToSet });
      message.success(`Selected orders marked as ${statusToSet}`);
      setSelectedReplacementRowKeys([]);
      loadAllData();
    } catch (error) {
      message.error('Failed to update status');
      setLoading(false);
    }
  };

  const handleAddType = () => {
    const trimmed = (newTypeName || '').trim();
    if (!trimmed) {
      message.warning('Please enter a type name');
      return;
    }
    
    const updatedCustom = Array.from(new Set([...customTypes, trimmed]));
    setCustomTypes(updatedCustom);
    try {
      localStorage.setItem('equipment_replacement_types', JSON.stringify(updatedCustom));
    } catch (e) {}

    const updatedDeleted = deletedTypes.filter(d => d.toLowerCase() !== trimmed.toLowerCase());
    setDeletedTypes(updatedDeleted);
    try {
      localStorage.setItem('equipment_deleted_replacement_types', JSON.stringify(updatedDeleted));
    } catch (e) {}

    form.setFieldsValue({ type: trimmed });
    setNewTypeName('');
    message.success(`Type "${trimmed}" added`);
  };

  const handleStartEditType = (typeToEdit) => {
    setEditingTypeTarget({ oldName: typeToEdit, newName: typeToEdit });
    setIsEditTypeModalVisible(true);
  };

  const handleSaveEditedType = async () => {
    if (!editingTypeTarget || !editingTypeTarget.newName || !editingTypeTarget.newName.trim()) {
      message.warning('Please enter a valid type name');
      return;
    }
    const oldName = editingTypeTarget.oldName;
    const newName = editingTypeTarget.newName.trim();
    if (oldName === newName) {
      setIsEditTypeModalVisible(false);
      return;
    }

    try {
      await renameReplacementType(oldName, newName);
    } catch (err) {
      console.error('Failed to rename type on server:', err);
    }

    const updatedCustom = customTypes.map(t => t === oldName ? newName : t);
    if (!updatedCustom.includes(newName)) updatedCustom.push(newName);
    setCustomTypes(updatedCustom);
    try {
      localStorage.setItem('equipment_replacement_types', JSON.stringify(updatedCustom));
    } catch (e) {}

    if (form.getFieldValue('type') === oldName) {
      form.setFieldsValue({ type: newName });
    }

    setReplacementOrdersData(prev => 
      prev.map(item => item.type === oldName ? { ...item, type: newName } : item)
    );

    setIsEditTypeModalVisible(false);
    setEditingTypeTarget(null);
    message.success(`Type renamed to "${newName}"`);
    loadAllData();
  };

  const handleDeleteType = async (typeToDelete) => {
    try {
      await deleteReplacementType(typeToDelete);
    } catch (err) {
      console.error('Failed to delete type on server:', err);
    }

    const updatedCustom = customTypes.filter(t => t !== typeToDelete);
    setCustomTypes(updatedCustom);
    try {
      localStorage.setItem('equipment_replacement_types', JSON.stringify(updatedCustom));
    } catch (e) {}

    const updatedDeleted = Array.from(new Set([...deletedTypes, typeToDelete]));
    setDeletedTypes(updatedDeleted);
    try {
      localStorage.setItem('equipment_deleted_replacement_types', JSON.stringify(updatedDeleted));
    } catch (e) {}

    if (form.getFieldValue('type') === typeToDelete) {
      form.setFieldsValue({ type: undefined });
    }

    setReplacementOrdersData(prev => 
      prev.map(item => item.type === typeToDelete ? { ...item, type: null } : item)
    );

    message.success(`Type "${typeToDelete}" deleted`);
    loadAllData();
  };

  const openEditModal = (type, record) => {
    setModalType(type);
    setIsEditing(true);
    let id;
    if (type === 'missing-items') id = record.missing_id;
    else if (type === 'removed-installed') id = record.removed_id;
    else if (type === 'replacement-orders') id = record.replacement_id;
    
    setEditingId(id);
    form.resetFields();
    form.setFieldsValue(record);
    setIsModalVisible(true);
    if (record.cluster_id) {
      loadInstitutionsForForm(record.cluster_id, record.academic_year_id || filterAcademicYear);
    }
  };

  const openEditInvoice = async (record) => {
    setModalType('new-equipment');
    setIsEditing(true);
    setEditingId(record.invoice_id);
    form.resetFields();
    
    // Find all items for this invoice
    const invoiceItems = newEquipmentData.filter(item => item.invoice_id === record.invoice_id);
    
    // Extract unique cluster IDs across invoice items and current context
    const clusterIds = Array.from(new Set([
      ...invoiceItems.map(i => i.cluster_id),
      record.cluster_id,
      filterCluster
    ].filter(Boolean)));

    if (clusterIds.length > 0) {
      await loadInstitutionsForForm(clusterIds, record.academic_year_id || filterAcademicYear);
    }

    const invoiceData = {
      ...record,
      academic_year_id: record.academic_year_id || filterAcademicYear,
      items: invoiceItems.map(item => ({
        ...item,
        nos: item.nos || 1
      }))
    };

    form.setFieldsValue(invoiceData);
    setIsModalVisible(true);
  };

  const handleAddSubmit = async (values) => {
    try {
      // For new equipment, extract cluster_id from the first item to attach to the invoice
      if (modalType === 'new-equipment' && values.items && values.items.length > 0) {
        const firstWithCluster = values.items.find(i => i && i.cluster_id);
        if (firstWithCluster) {
          values.cluster_id = firstWithCluster.cluster_id;
        }
      }
      
      if (modalType === 'replacement-orders') {
        if (!values.status || (role !== 'ADMIN' && !isEditing)) {
          values.status = 'Pending';
        }
      }

      if (isEditing) {
        if (modalType === 'new-equipment') await updateNewEquipmentInvoice(editingId, values);
        else if (modalType === 'missing-items') await updateMissingItem(editingId, values);
        else if (modalType === 'removed-installed') await updateRemovedItem(editingId, values);
        else if (modalType === 'replacement-orders') await updateReplacementOrder(editingId, values);
        message.success('Record updated successfully');
      } else {
        if (modalType === 'new-equipment') await createNewEquipmentInvoice(values);
        else if (modalType === 'missing-items') await createMissingItem(values);
        else if (modalType === 'removed-installed') await createRemovedItem(values);
        else if (modalType === 'replacement-orders') await createReplacementOrder(values);
        message.success('Record(s) created successfully');
      }
      setIsModalVisible(false);
      loadAllData();
    } catch (error) {
      console.error(error);
      message.error(isEditing ? 'Failed to update' : 'Failed to create');
    }
  };

  const handleSchoolChange = (val, type, fieldName = null) => {
    const selectedInst = formInstitutions.find(inst => String(inst.cluster_institution_id) === String(val) || String(inst.institution_id) === String(val));
    if (selectedInst) {
      const code = selectedInst.cluster_institution_code || `ID:${selectedInst.institution_id}`;
      const name = selectedInst.institution_name;
      const cname = clusters.find(c => String(c.cluster_id) === String(selectedInst.cluster_id))?.cluster_name || '';

      if (type === 'new-equipment-item') {
        const items = form.getFieldValue('items') || [];
        items[fieldName] = { 
          ...items[fieldName], 
          s_code: code, 
          institution_name: name, 
          cluster_name: cname,
          cluster_id: items[fieldName]?.cluster_id || selectedInst.cluster_id,
          cluster_institution_id: selectedInst.cluster_institution_id
        };
        form.setFieldsValue({ items });
        setUpdateState(prev => prev + 1);
      } else if (type === 'removed-installed-removed') {
        form.setFieldsValue({ removed_school_name: `${code} - ${name}` });
      } else if (type === 'removed-installed-installed') {
        form.setFieldsValue({ installed_school_name: `${code} - ${name}` });
      } else {
        form.setFieldsValue({ s_code: code });
        if (modalType === 'replacement-orders') {
          form.setFieldsValue({ school_name: name });
        } else {
          form.setFieldsValue({ school: name });
        }
      }
    }
  };

  const sharedOnCell = (record, key) => {
    if (['invoice_no', 'invoice_date', 'shop_name', 'paid_by', 'discount', 'total_gst', 'round_off', 'grand_total', 'action'].includes(key)) {
      return { rowSpan: record.invRowSpan === undefined ? 1 : record.invRowSpan };
    }
    if (key === 'total_amount') {
      return { rowSpan: record.itemRowSpan === undefined ? 1 : record.itemRowSpan };
    }
    return {};
  };

  const ActionColumn = () => ({
    title: 'ACTION',
    key: 'action',
    fixed: 'right',
    width: 100,
    render: (_, record) => (
      <Space>
        <Button type="text" icon={<EditOutlined />} onClick={() => openEditModal(activeTab, record)} />
        <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.key)}>
          <Button danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      </Space>
    )
  });

  const handleDeleteInvoice = async (invoiceId) => {
    try {
      await deleteNewEquipmentInvoice(invoiceId);
      message.success('Invoice deleted successfully');
      loadAllData();
    } catch (error) {
      message.error('Failed to delete invoice');
    }
  };

  // --- 1. New Equipment List ---
  const newEquipmentColumns = [
    { title: 'CLUSTER', dataIndex: 'cluster_name', key: 'cluster_name' },
    { title: 'S-CODE', dataIndex: 's_code', key: 's_code', render: text => text ? <Tag color="blue" style={{ fontFamily: 'monospace', fontWeight: 600, borderRadius: 2 }}>{text}</Tag> : '' },
    { title: 'SCHOOL', dataIndex: 'institution_name', key: 'institution_name' },
    { 
      title: 'ITEM', 
      dataIndex: 'item_name', 
      key: 'item_name', 
      width: 240,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{text}</div>
          {record.serial_number && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              s/n: <span style={{ fontFamily: 'monospace' }}>{record.serial_number}</span>
            </div>
          )}
        </div>
      )
    },
    { title: 'QTY', dataIndex: 'nos', key: 'nos', align: 'center' },
    { title: 'RATE', dataIndex: 'rate', key: 'rate', align: 'right', render: v => v ? `₹ ${v}` : '' },
    { title: 'AMOUNT', dataIndex: 'total_amount', key: 'total_amount', align: 'right', onCell: (record) => sharedOnCell(record, 'total_amount'), render: v => v ? `₹ ${v}` : '' },
    { title: 'DISCOUNT', dataIndex: 'discount', key: 'discount', align: 'right', onCell: (record) => sharedOnCell(record, 'discount'), render: v => v ? `₹ ${v}` : '' },
    { title: 'GST', dataIndex: 'total_gst', key: 'total_gst', align: 'right', onCell: (record) => sharedOnCell(record, 'total_gst'), render: v => v ? `₹ ${v}` : '' },
    { title: 'Round Off', dataIndex: 'round_off', key: 'round_off', align: 'right', onCell: (record) => sharedOnCell(record, 'round_off'), render: v => v ? `₹ ${v}` : '' },
    { title: 'TOTAL', dataIndex: 'grand_total', key: 'grand_total', align: 'right', onCell: (record) => sharedOnCell(record, 'grand_total'), render: v => <strong style={{ color: '#1890ff' }}>₹ {v}</strong> },
    { title: 'SHOPNAME', dataIndex: 'shop_name', key: 'shop_name', onCell: (record) => sharedOnCell(record, 'shop_name') },
    { title: 'PAID BY', dataIndex: 'paid_by', key: 'paid_by', onCell: (record) => sharedOnCell(record, 'paid_by') },
    { title: 'INVOICE DATE', dataIndex: 'invoice_date', key: 'invoice_date', onCell: (record) => sharedOnCell(record, 'invoice_date') },
    { title: 'INVOICE NO', dataIndex: 'invoice_no', key: 'invoice_no', onCell: (record) => sharedOnCell(record, 'invoice_no'), render: text => text ? <Tag color="cyan" style={{ fontFamily: 'monospace', borderRadius: 2 }}>{text}</Tag> : '' },
    { 
      title: 'ACTION', key: 'action', fixed: 'right', width: 100,
      onCell: (record) => sharedOnCell(record, 'action'),
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => openEditInvoice(record)} />
          <Popconfirm title="Sure to delete invoice?" onConfirm={() => handleDeleteInvoice(record.invoice_id)}>
            <Button danger type="text" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // --- 2. Missing Items ---
  const missingItemsColumns = [
    { title: 'CLUSTER', dataIndex: 'cluster_name', key: 'cluster_name' },
    { title: 'S-CODE', dataIndex: 's_code', key: 's_code' },
    { title: 'INSTITUTION NAME', dataIndex: 'school', key: 'school' },
    { title: 'YEAR', dataIndex: 'year', key: 'year' },
    { title: 'ITEM', dataIndex: 'item', key: 'item' },
    { title: 'PRICE (ETM)', dataIndex: 'price_etm', key: 'price_etm', render: (text) => text ? `₹ ${text}` : '' },
    ActionColumn()
  ];

  // --- 3. Removed & Installed ---
  const removedInstalledColumns = [
    { title: 'CLUSTER', dataIndex: 'cluster_name', key: 'cluster_name' },
    { title: 'REMOVED INSTITUTION NAME', dataIndex: 'removed_school_name', key: 'removed_school_name' },
    { title: 'REMOVED ITEMS', dataIndex: 'removed_items', key: 'removed_items' },
    { title: 'INSTALLED CLUSTER', dataIndex: 'installed_cluster_name', key: 'installed_cluster_name' },
    { title: 'INSTALLED INSTITUTION NAME', dataIndex: 'installed_school_name', key: 'installed_school_name' },
    { title: 'INSTALL', dataIndex: 'install_items', key: 'install_items' },
    { title: 'REMARK', dataIndex: 'remark', key: 'remark' },
    ActionColumn()
  ];

  // --- 4. Replacement Orders ---
  const replacementOrdersColumns = [
    { 
      title: 'S-CODE', 
      dataIndex: 's_code', 
      key: 's_code',
      render: text => text ? <Tag color="blue" style={{ fontFamily: 'monospace', fontWeight: 600, borderRadius: 2 }}>{text}</Tag> : ''
    },
    { title: 'CLUSTER', dataIndex: 'cluster_name', key: 'cluster_name' },
    { title: 'INSTITUTION NAME', dataIndex: 'school_name', key: 'school_name' },
    { title: 'YEAR', dataIndex: 'year', key: 'year' },
    { 
      title: 'TYPE', 
      dataIndex: 'type', 
      key: 'type',
      render: text => text ? <Tag color="geekblue">{text}</Tag> : ''
    },
    { title: 'EQUIPMENT NAME', dataIndex: 'equipment', key: 'equipment' },
    { title: 'NOS', dataIndex: 'nos', key: 'nos', align: 'center' },
    { 
      title: 'STATUS', 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => {
        const isOrdered = (status || 'Pending').toLowerCase() === 'ordered';
        return <Tag color={isOrdered ? 'green' : 'gold'}>{status || 'Pending'}</Tag>;
      }
    },
    { title: 'REMARKS', dataIndex: 'remarks', key: 'remarks' },
    ActionColumn()
  ];

  const pendingSummaryColumns = [
    { title: 'EQUIPMENT NAME', dataIndex: 'type', key: 'type' },
    { title: 'NO\'S', dataIndex: 'nos', key: 'nos' }
  ];

  const getPendingSummaryData = () => {
    const pendingOrders = replacementOrdersData.filter(r => r.status === 'Pending' && r.type);
    const summary = {};
    pendingOrders.forEach(order => {
      if (!summary[order.type]) {
        summary[order.type] = 0;
      }
      summary[order.type] += (Number(order.nos) || 0);
    });
    return Object.keys(summary).map((type, index) => ({
      key: `summary-${index}`,
      type: type,
      nos: summary[type]
    }));
  };

  const renderSchoolSelect = (type = 'default', fieldName = null) => {
    let filteredInstitutions = formInstitutions;
    
    if (type === 'new-equipment-item' && fieldName !== null) {
      const items = form.getFieldValue('items') || [];
      const rowClusterId = items[fieldName]?.cluster_id;
      if (rowClusterId) {
        filteredInstitutions = formInstitutions.filter(inst => String(inst.cluster_id) === String(rowClusterId));
      }
    } else if (type === 'removed-installed-installed') {
       const instClusterId = form.getFieldValue('installed_cluster_id');
       if (instClusterId) {
         filteredInstitutions = formInstitutions.filter(inst => String(inst.cluster_id) === String(instClusterId));
       }
    } else if (type === 'removed-installed-removed') {
       const remClusterId = form.getFieldValue('cluster_id');
       if (remClusterId) {
         filteredInstitutions = formInstitutions.filter(inst => String(inst.cluster_id) === String(remClusterId));
       }
    }

    let currentValue = undefined;
    if (type === 'new-equipment-item' && fieldName !== null) {
      const items = form.getFieldValue('items') || [];
      const rowItem = items[fieldName] || {};
      if (rowItem.cluster_institution_id) {
        currentValue = rowItem.cluster_institution_id;
      } else if (rowItem.institution_name) {
        const found = formInstitutions.find(inst => 
          inst.institution_name?.toLowerCase() === rowItem.institution_name?.toLowerCase() ||
          inst.cluster_institution_code?.toLowerCase() === rowItem.s_code?.toLowerCase()
        );
        if (found) currentValue = found.cluster_institution_id;
      }
    }

    return (
      <Select 
        value={currentValue}
        placeholder="Select Institution" 
        onChange={(val) => handleSchoolChange(val, type, fieldName)} 
        showSearch 
        optionFilterProp="children" 
        allowClear
        style={{ width: '100%' }}
      >
        {filteredInstitutions.map(inst => {
          const code = inst.cluster_institution_code || `ID:${inst.institution_id}`;
          return (
            <Option key={inst.cluster_institution_id} value={inst.cluster_institution_id}>
              {`${code} - ${inst.institution_name}`}
            </Option>
          );
        })}
      </Select>
    );
  };

  const renderContextFilters = (requireCluster = false) => (
    <Space style={{ display: 'flex', marginBottom: 16 }}>
      <Form.Item name="academic_year_id" label="Academic Year" style={{ width: 200, marginBottom: 0 }}>
        <Select placeholder="Select Academic Year" allowClear>
          {academicYears.map(ay => (
            <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item name="cluster_id" label="Cluster" style={{ width: 200, marginBottom: 0 }} rules={requireCluster ? [{ required: true }] : []}>
        <Select placeholder="Select Cluster" allowClear onChange={handleClusterSelect}>
          {getAvailableClusters(formAcademicYearId).map(c => (
            <Option key={c.cluster_id} value={c.cluster_id}>{c.cluster_name}</Option>
          ))}
        </Select>
      </Form.Item>
    </Space>
  );

  const uniqueShopNames = [...new Set(newEquipmentData.map(item => item.shop_name).filter(Boolean))].map(name => ({ value: name }));
  
  const uniquePaidBy = [...new Set([
    'Manipal Foundation', 
    'Mahamaya Foundation', 
    ...newEquipmentData.map(item => item.paid_by)
  ].filter(Boolean))].map(name => ({ value: name }));

  const renderModalForm = () => {
    switch(modalType) {
      case 'new-equipment':
        // Invoice + Multiple Items for both Create and Edit
        return (
          <>
            <div className="excel-header-bar">
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item name="academic_year_id" label="Academic Year" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Select placeholder="Select Academic Year" allowClear>
                      {academicYears.map(ay => (
                        <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="invoice_date" label="Invoice Date" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="invoice_no" label="Invoice Number" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Input placeholder="INV-001" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name="paid_by" label="Paid By" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <AutoComplete options={uniquePaidBy} placeholder="Select or type Payer" filterOption={(inputValue, option) => option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 12 }}>
                <Col span={12}>
                  <Form.Item name="shop_name" label="Shop / Vendor Name" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <AutoComplete options={uniqueShopNames} placeholder="Select or type Shop Name" filterOption={(inputValue, option) => option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table className="excel-form-table">
                <thead>
                  <tr>
                    <th style={{ width: '13%' }}>CLUSTER</th>
                    <th style={{ width: '22%' }}>CODE / INSTITUTION</th>
                    <th style={{ width: '20%' }}>ITEM NAME</th>
                    <th style={{ width: '15%' }}>SERIAL NUMBER</th>
                    <th style={{ width: '9%' }}>RATE (₹)</th>
                    <th style={{ width: '6%' }}>NOS</th>
                    <th style={{ width: '7%' }}>GST %</th>
                    <th style={{ width: '10%' }}>PRICE (₹)</th>
                    <th style={{ width: '4%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  <Form.List name="items">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name, ...restField }) => (
                          <tr key={key}>
                            <td>
                              <Form.Item {...restField} name={[name, 'cluster_id']} style={{ marginBottom: 0 }}>
                                <Select 
                                  placeholder="Cluster" 
                                  onChange={(val) => handleRowClusterChange(val, name)} 
                                  showSearch 
                                  optionFilterProp="children" 
                                  allowClear
                                  style={{ width: '100%' }}
                                >
                                  {getAvailableClusters(formAcademicYearId).map(c => (
                                    <Option key={c.cluster_id} value={c.cluster_id}>{c.cluster_name}</Option>
                                  ))}
                                </Select>
                              </Form.Item>
                              <Form.Item {...restField} name={[name, 'cluster_name']} hidden><Input /></Form.Item>
                            </td>
                            <td>
                              <Form.Item style={{ marginBottom: 0 }}>
                                {renderSchoolSelect('new-equipment-item', name)}
                              </Form.Item>
                              <Form.Item {...restField} name={[name, 's_code']} hidden><Input /></Form.Item>
                              <Form.Item {...restField} name={[name, 'institution_name']} hidden><Input /></Form.Item>
                            </td>
                            <td>
                              <Form.Item {...restField} name={[name, 'item_name']} style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Required' }]}>
                                <Input placeholder="Item Name" className="excel-cell-input" />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item {...restField} name={[name, 'serial_number']} style={{ marginBottom: 0 }}>
                                <Input placeholder="Serial" className="excel-cell-input" />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item {...restField} name={[name, 'rate']} style={{ marginBottom: 0 }}>
                                <Input placeholder="0.00" prefix="₹" className="excel-cell-input" />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item {...restField} name={[name, 'nos']} style={{ marginBottom: 0 }}>
                                <InputNumber min={1} style={{ width: '100%' }} placeholder="Qty" className="excel-cell-input" />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item {...restField} name={[name, 'gst_percent']} style={{ marginBottom: 0 }}>
                                <Input placeholder="%" className="excel-cell-input" />
                              </Form.Item>
                            </td>
                            <td>
                              <Form.Item {...restField} name={[name, 'price']} style={{ marginBottom: 0 }}>
                                <Input placeholder="0.00" prefix="₹" readOnly className="excel-cell-input excel-cell-readonly" />
                              </Form.Item>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => {
                                remove(name);
                                setTimeout(() => {
                                  const allVals = form.getFieldsValue();
                                  handleValuesChange({ items: allVals.items }, allVals);
                                }, 50);
                              }} />
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={9} style={{ padding: '10px 8px', background: 'var(--excel-zebra-bg, #f8fafc)' }}>
                            <Button type="dashed" onClick={() => add({ nos: 1 })} block icon={<PlusOutlined />} style={{ borderRadius: 2, borderColor: 'var(--primary-color, #1890ff)', color: 'var(--primary-color, #1890ff)', fontWeight: 600 }}>
                              + Add Equipment Item Row
                            </Button>
                          </td>
                        </tr>
                      </>
                    )}
                  </Form.List>
                </tbody>
              </table>
            </div>

            <Row justify="end">
              <Col span={9} className="excel-summary-box">
                <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={10} style={{ textAlign: 'right', fontWeight: 500 }}>Sub Total:</Col>
                  <Col span={14}>
                    <Form.Item name="sub_total" style={{ marginBottom: 0 }}>
                      <Input prefix="₹" readOnly className="excel-cell-readonly" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={10} style={{ textAlign: 'right', fontWeight: 500 }}>Discount:</Col>
                  <Col span={14}>
                    <Form.Item name="discount" style={{ marginBottom: 0 }}>
                      <Input prefix="₹" className="excel-cell-input" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={10} style={{ textAlign: 'right', fontWeight: 500 }}>GST:</Col>
                  <Col span={14}>
                    <Form.Item name="total_gst" style={{ marginBottom: 0 }}>
                      <Input prefix="₹" readOnly className="excel-cell-readonly" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
                  <Col span={10} style={{ textAlign: 'right', fontWeight: 500 }}>Round Off:</Col>
                  <Col span={14}>
                    <Form.Item name="round_off" style={{ marginBottom: 0 }}>
                      <Input prefix="₹" readOnly className="excel-cell-readonly" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16} align="middle">
                  <Col span={10} style={{ textAlign: 'right', fontWeight: 'bold', color: 'var(--primary-color, #1890ff)' }}>Grand Total:</Col>
                  <Col span={14}>
                    <Form.Item name="grand_total" style={{ marginBottom: 0 }}>
                      <Input prefix="₹" style={{ fontWeight: 'bold', color: 'var(--primary-color, #1890ff)' }} readOnly className="excel-cell-readonly" />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        );
      case 'missing-items':
        return (
          <>
            {renderContextFilters(true)}
            <Form.Item label="Select Institution (Auto-fills Code & Name)">{renderSchoolSelect()}</Form.Item>
            <Form.Item name="s_code" label="S-CODE" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="school" label="INSTITUTION NAME" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="year" label="YEAR"><Input /></Form.Item>
            <Form.Item name="item" label="ITEM" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="price_etm" label="PRICE (ETM)"><Input /></Form.Item>
          </>
        );
      case 'removed-installed':
        return (
          <>
            {renderContextFilters(true)}
            <Form.Item label="Select Removed Institution (Auto-fills Name)">{renderSchoolSelect('removed-installed-removed')}</Form.Item>
            <Form.Item name="removed_school_name" label="REMOVED INSTITUTION NAME" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="removed_items" label="REMOVED ITEMS"><Input.TextArea /></Form.Item>
            <Form.Item name="installed_cluster_id" label="INSTALLED CLUSTER">
              <Select placeholder="Select Installed Cluster" allowClear onChange={handleInstalledClusterSelect}>
                {getAvailableClusters(formAcademicYearId).map(c => (
                  <Option key={c.cluster_id} value={c.cluster_id}>{c.cluster_name}</Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="Select Installed Institution (Auto-fills Name)">{renderSchoolSelect('removed-installed-installed')}</Form.Item>
            <Form.Item name="installed_school_name" label="INSTALLED INSTITUTION NAME"><Input /></Form.Item>
            <Form.Item name="install_items" label="INSTALL ITEMS"><Input.TextArea /></Form.Item>
            <Form.Item name="remark" label="REMARK"><Input /></Form.Item>
          </>
        );
      case 'replacement-orders':
        return (
          <>
            {renderContextFilters(true)}
            <Form.Item label="Select Institution (Auto-fills Code & Name)">{renderSchoolSelect()}</Form.Item>
            <Form.Item name="s_code" label="S-CODE" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="school_name" label="INSTITUTION NAME" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="year" label="YEAR"><Input /></Form.Item>
            <Form.Item name="type" label="TYPE" rules={[{ required: true, message: 'Please select or add a type' }]}>
              <Select
                placeholder="Select or add type"
                showSearch
                optionFilterProp="value"
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', gap: 6, padding: '4px 8px 8px' }}>
                      <Input
                        placeholder="Please enter new type"
                        value={newTypeName}
                        onChange={(e) => setNewTypeName(e.target.value)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddType();
                          }
                        }}
                      />
                      <Button type="primary" size="middle" icon={<PlusOutlined />} onClick={handleAddType}>
                        Add
                      </Button>
                    </div>
                  </>
                )}
              >
                {Array.from(
                  new Set([
                    ...customTypes,
                    ...(replacementOrdersData || []).map(r => r.type).filter(Boolean)
                  ])
                )
                  .filter(t => !deletedTypes.includes(t))
                  .map(t => (
                    <Option key={t} value={t}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span style={{ fontWeight: 500 }}>{t}</span>
                        <Space size={4} onClick={(e) => e.stopPropagation()}>
                          <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined style={{ fontSize: 13, color: '#1890ff' }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleStartEditType(t);
                            }}
                            title="Edit Type"
                            style={{ height: 24, width: 24, padding: 0 }}
                          />
                          <Popconfirm
                            title={`Delete type "${t}"?`}
                            description="This will remove this type from the list."
                            onConfirm={(e) => {
                              if (e) e.stopPropagation();
                              handleDeleteType(t);
                            }}
                            onCancel={(e) => e && e.stopPropagation()}
                            okText="Delete"
                            cancelText="Cancel"
                          >
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined style={{ fontSize: 13 }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                              }}
                              title="Delete Type"
                              style={{ height: 24, width: 24, padding: 0 }}
                            />
                          </Popconfirm>
                        </Space>
                      </div>
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item name="equipment" label="EQUIPMENT NAME" rules={[{ required: true }]}><Input /></Form.Item>
            <Form.Item name="nos" label="NOS"><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
            <Form.Item name="status" label="STATUS" rules={[{ required: true }]} initialValue="Pending">
              <Select placeholder="Select status" disabled={role !== 'ADMIN'}>
                <Option value="Pending">Pending</Option>
                <Option value="Ordered">Ordered</Option>
              </Select>
            </Form.Item>
            <Form.Item name="remarks" label="REMARKS"><Input /></Form.Item>
          </>
        );
      default: return null;
    }
  };

  const getModalTitle = () => {
    const prefix = isEditing ? 'Edit' : 'Add';
    switch(modalType) {
      case 'new-equipment': return isEditing ? 'Edit Equipment Item' : 'New Equipment Invoice';
      case 'missing-items': return `${prefix} Missing Item`;
      case 'removed-installed': return `${prefix} Removed & Installed Record`;
      case 'replacement-orders': return `${prefix} Replacement Order`;
      default: return `${prefix} Record`;
    }
  };

  const handleSearchChange = (tabKey, value) => {
    setSearchTexts(prev => ({ ...prev, [tabKey]: value }));
  };

  const getFilteredData = (data, tabKey) => {
    const text = searchTexts[tabKey];
    if (!text) return data;
    const lowercasedSearch = text.toLowerCase();
    return data.filter(item => {
      return Object.values(item).some(val => 
        val !== null && val !== undefined && String(val).toLowerCase().includes(lowercasedSearch)
      );
    });
  };

  const getFilteredReplacementData = () => {
    let data = getFilteredData(replacementOrdersData, 'replacement-orders');
    if (filterReplacementStatus && filterReplacementStatus !== 'ALL') {
      data = data.filter(item => (item.status || 'Pending').toLowerCase() === filterReplacementStatus.toLowerCase());
    }
    return data;
  };

  const exportReplacementOrdersCSV = () => {
    const dataToExport = getFilteredReplacementData();
    if (!dataToExport || dataToExport.length === 0) {
      message.warning('No replacement orders to export');
      return;
    }

    const headers = [
      'S-CODE',
      'CLUSTER',
      'INSTITUTION NAME',
      'YEAR',
      'TYPE',
      'EQUIPMENT NAME',
      'NOS',
      'STATUS',
      'REMARKS'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      const str = String(val).replace(/"/g, '""');
      return `"${str}"`;
    };

    const rows = dataToExport.map(item => [
      escapeCSV(item.s_code || ''),
      escapeCSV(item.cluster_name || ''),
      escapeCSV(item.school_name || ''),
      escapeCSV(item.year || ''),
      escapeCSV(item.type || ''),
      escapeCSV(item.equipment || ''),
      escapeCSV(item.nos || 0),
      escapeCSV(item.status || 'Pending'),
      escapeCSV(item.remarks || '')
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const dateStr = new Date().toISOString().slice(0, 10);
    const statusSuffix = filterReplacementStatus !== 'ALL' ? `_${filterReplacementStatus}` : '';
    link.setAttribute('download', `Replacement_Orders${statusSuffix}_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('Replacement Orders CSV downloaded successfully');
  };

  const getGroupedEquipmentData = () => {
    let filtered = getFilteredData(newEquipmentData, 'new-equipment');

    // Group by invoice_id
    const invoiceGroups = {};
    filtered.forEach(item => {
      const invId = item.invoice_id || item.key;
      if (!invoiceGroups[invId]) invoiceGroups[invId] = [];
      invoiceGroups[invId].push(item);
    });

    const processed = [];
    Object.values(invoiceGroups).forEach(group => {
      const invRowSpan = group.length;
      
      const itemGroups = {};
      group.forEach(item => {
        const itemName = item.item_name || 'unknown';
        if (!itemGroups[itemName]) itemGroups[itemName] = [];
        itemGroups[itemName].push(item);
      });

      let firstOfInvoice = true;
      Object.values(itemGroups).forEach(items => {
        const itemRowSpan = items.length;
        const totalAmount = items.reduce((sum, it) => sum + (parseFloat(it.price) || 0), 0);
        
        items.forEach((item, index) => {
          const newItem = { ...item };
          
          if (firstOfInvoice) {
            newItem.invRowSpan = invRowSpan;
            firstOfInvoice = false;
          } else {
            newItem.invRowSpan = 0;
          }

          if (index === 0) {
            newItem.itemRowSpan = itemRowSpan;
            newItem.total_amount = totalAmount.toFixed(2);
          } else {
            newItem.itemRowSpan = 0;
          }

          processed.push(newItem);
        });
      });
    });

    return processed;
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    const tableData = getGroupedEquipmentData();
    
    const tableColumn = [
      "CLUSTER", "S-CODE", "SCHOOL", "ITEM", "QTY", "RATE", "AMOUNT", 
      "DISCOUNT", "GST", "Round Off", "TOTAL", "SHOPNAME", "PAID BY", 
      "INVOICE DATE", "INVOICE NO"
    ];

    const tableRows = [];

    tableData.forEach(item => {
      const itemData = [];
      itemData.push(item.cluster_name || '');
      itemData.push(item.s_code || '');
      itemData.push(item.institution_name || '');
      const itemDisplay = item.serial_number ? `${item.item_name || ''}\ns/n: ${item.serial_number}` : (item.item_name || '');
      itemData.push(itemDisplay);
      itemData.push(item.nos || '');
      itemData.push(item.rate || '');

      if (item.itemRowSpan > 0) {
        itemData.push({ content: item.total_amount || '', rowSpan: item.itemRowSpan, styles: { valign: 'middle', halign: 'center' } });
      }

      if (item.invRowSpan > 0) {
        itemData.push({ content: item.discount || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
        itemData.push({ content: item.total_gst || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
        itemData.push({ content: item.round_off || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
        itemData.push({ content: item.grand_total || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
        itemData.push({ content: item.shop_name || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
        itemData.push({ content: item.paid_by || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
        itemData.push({ content: item.invoice_date || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
        itemData.push({ content: item.invoice_no || '', rowSpan: item.invRowSpan, styles: { valign: 'middle', halign: 'center' } });
      }
      tableRows.push(itemData);
    });

    const currentDateTime = new Date().toLocaleString('en-IN');
    doc.setFontSize(14);
    doc.text("NEW EQUIPMENT LIST", 14, 15);
    doc.setFontSize(10);
    doc.text(`Generated on: ${currentDateTime}`, 14, 22);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 26,
      styles: { fontSize: 7, cellPadding: 1 },
      headStyles: { fillColor: [41, 128, 185], fontSize: 7 },
    });

    doc.save("New_Equipment_List.pdf");
  };

  const tabItems = [
    {
      label: (<span><AppstoreAddOutlined /> New Equipment List</span>),
      key: 'new-equipment',
      children: (
        <Card 
          title="NEW EQUIPMENT LIST" 
          bordered={false} 
          className="shadow-sm"
          extra={
            <Space>
              <Input.Search
                placeholder="Search New Equipment..."
                allowClear
                onChange={(e) => handleSearchChange('new-equipment', e.target.value)}
                style={{ width: 250 }}
              />
              <Button type="default" icon={<DownloadOutlined />} onClick={exportToPDF}>Export PDF</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openAddModal('new-equipment')}>Add Invoice Record</Button>
            </Space>
          }
        >
          <Table 
            columns={newEquipmentColumns} 
            dataSource={getGroupedEquipmentData()} 
            scroll={{ x: 'max-content' }} 
            pagination={{ pageSize: 100 }}
            bordered
            size="middle"
            loading={loading}
          />
        </Card>
      ),
    },
    {
      label: (<span><WarningOutlined /> Missing Items</span>),
      key: 'missing-items',
      children: (
        <Card 
          title="MISSING ITEM" 
          bordered={false} 
          className="shadow-sm"
          extra={
            <Space>
              <Input.Search
                placeholder="Search Missing Items..."
                allowClear
                onChange={(e) => handleSearchChange('missing-items', e.target.value)}
                style={{ width: 250 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openAddModal('missing-items')}>Add Missing Item</Button>
            </Space>
          }
        >
          <Table 
            columns={missingItemsColumns} 
            dataSource={getFilteredData(missingItemsData, 'missing-items')} 
            scroll={{ x: 'max-content' }} 
            pagination={{ pageSize: 10 }}
            bordered
            size="middle"
            loading={loading}
          />
        </Card>
      ),
    },
    {
      label: (<span><SwapOutlined /> Removed & Installed</span>),
      key: 'removed-installed',
      children: (
        <Card 
          title="REMOVED & INSTALLED TO THESE INSTITUTIONS" 
          bordered={false} 
          className="shadow-sm"
          extra={
            <Space>
              <Input.Search
                placeholder="Search Removed/Installed..."
                allowClear
                onChange={(e) => handleSearchChange('removed-installed', e.target.value)}
                style={{ width: 250 }}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openAddModal('removed-installed')}>Add Record</Button>
            </Space>
          }
        >
          <Table 
            columns={removedInstalledColumns} 
            dataSource={getFilteredData(removedInstalledData, 'removed-installed')} 
            scroll={{ x: 'max-content' }} 
            pagination={{ pageSize: 10 }}
            bordered
            size="middle"
            loading={loading}
          />
        </Card>
      ),
    },
    {
      label: (<span><SyncOutlined /> Replacement Orders</span>),
      key: 'replacement-orders',
      children: (
        <Card 
          title="Replacement Orders" 
          bordered={false} 
          className="shadow-sm"
          extra={
            <Space wrap>
              <Select
                value={filterReplacementStatus}
                onChange={setFilterReplacementStatus}
                style={{ width: 130 }}
                placeholder="Status"
                suffixIcon={<FilterOutlined />}
              >
                <Option value="ALL">All Status</Option>
                <Option value="Pending">Pending</Option>
                <Option value="Ordered">Ordered</Option>
              </Select>
              {selectedReplacementRowKeys.length > 0 && role === 'ADMIN' && (
                <Space>
                  <Button 
                    type="primary" 
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} 
                    onClick={() => handleBulkStatusUpdate('Ordered')}
                  >
                    Mark {selectedReplacementRowKeys.length} Ordered
                  </Button>
                  <Button 
                    style={{ borderColor: '#faad14', color: '#faad14' }} 
                    onClick={() => handleBulkStatusUpdate('Pending')}
                  >
                    Mark Pending
                  </Button>
                </Space>
              )}
              <Input.Search
                placeholder="Search Replacement Orders..."
                allowClear
                onChange={(e) => handleSearchChange('replacement-orders', e.target.value)}
                style={{ width: 220 }}
              />
              <Button 
                icon={<DownloadOutlined />} 
                onClick={exportReplacementOrdersCSV}
              >
                Download CSV
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openAddModal('replacement-orders')}>
                Add Order
              </Button>
            </Space>
          }
        >
          <Table 
            columns={pendingSummaryColumns} 
            dataSource={getPendingSummaryData()} 
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 'max-content' }}
            style={{ marginBottom: 20, maxWidth: '100%' }}
          />
          <Table 
            rowSelection={{
              selectedRowKeys: selectedReplacementRowKeys,
              onChange: (newSelectedRowKeys) => setSelectedReplacementRowKeys(newSelectedRowKeys),
            }}
            columns={replacementOrdersColumns} 
            dataSource={getFilteredReplacementData()} 
            scroll={{ x: 'max-content' }} 
            pagination={{ pageSize: 10 }}
            bordered
            size="middle"
            loading={loading}
          />
        </Card>
      ),
    },
  ];

  return (
    <div className="equipment-management-wrapper" style={{ paddingBottom: '30px' }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>Equipment Management</Title>
        <Space>
          {activeTab !== 'new-equipment' && (
            <Select
              style={{ width: 200 }}
              placeholder="Filter by Cluster"
              allowClear={role !== 'STUDIO'}
              onChange={(val) => {
                if (!val && role === 'STUDIO') {
                  const available = getAvailableClusters(filterAcademicYear);
                  if (available.length > 0) {
                    setFilterCluster(available[0].cluster_id);
                    return;
                  }
                }
                setFilterCluster(val);
              }}
              value={filterCluster}
              suffixIcon={<FilterOutlined />}
            >
              {getAvailableClusters(filterAcademicYear).map(c => (
                <Option key={c.cluster_id} value={c.cluster_id}>{c.cluster_name}</Option>
              ))}
            </Select>
          )}

          <Select
            style={{ width: 200 }}
            placeholder="Filter by Academic Year"
            allowClear
            onChange={setFilterAcademicYear}
            value={filterAcademicYear}
            suffixIcon={<FilterOutlined />}
          >
            {academicYears.map(ay => (
              <Option key={ay.academic_year_id} value={ay.academic_year_id}>{ay.academic_year}</Option>
            ))}
          </Select>
        </Space>
      </div>

      <Card bordered={false} className="shadow-sm" style={{ padding: '0 10px' }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
        />
      </Card>

      <Modal
        title={getModalTitle()}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => form.submit()}
        destroyOnClose
        width={modalType === 'new-equipment' ? 1400 : 900}
      >
        <Form form={form} layout="vertical" onFinish={handleAddSubmit} onValuesChange={handleValuesChange}>
          {renderModalForm()}
        </Form>
      </Modal>

      {/* Edit Type Modal */}
      <Modal
        title="Edit Type Name"
        open={isEditTypeModalVisible}
        onOk={handleSaveEditedType}
        onCancel={() => {
          setIsEditTypeModalVisible(false);
          setEditingTypeTarget(null);
        }}
        okText="Save"
        cancelText="Cancel"
        destroyOnClose
        zIndex={1100}
      >
        <div style={{ padding: '12px 0' }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Type Name:</label>
          <Input
            value={editingTypeTarget?.newName || ''}
            onChange={(e) => setEditingTypeTarget(prev => ({ ...prev, newName: e.target.value }))}
            placeholder="Enter new type name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveEditedType();
              }
            }}
            autoFocus
          />
        </div>
      </Modal>
    </div>
  );
};

export default EquipmentManagementPage;
